import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../../../config";
import logo from "../../../assets/images/header-logo.png";
import "./AuthModal.css";

const AuthModal = ({ open, onClose, initialMode = "login" }) => {
  const { login, loginWithGoogle, loading, user } = useContext(AuthContext);
  const [mode, setMode] = useState(initialMode);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const redirectPath = location.state?.from || "/";

  // Reset form when mode changes or user logs in
  useEffect(() => {
    if (user) {
      setError("");
      setSuccess("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowPasswordSetup(false);
      setNewPassword("");
      setConfirmNewPassword("");
      onClose();
    }
  }, [user, onClose]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    setError("");
    setSuccess("");

    if (mode === "signup") {
      if (!isValidEmail(normalizedEmail)) {
        setError("Please enter a valid email address");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }

      try {
        await axios.post(`${config.apiBaseUrl}/api/auth/signup`, {
          firstName,
          lastName,
          email: normalizedEmail,
          password,
        });
        await login({ email: normalizedEmail, password }, redirectPath);
        setSuccess("Sign up successful! Redirecting...");
        setTimeout(() => {
          onClose();
          navigate(redirectPath);
        }, 1000);
      } catch (error) {
        console.error("Sign-up error:", error.response?.data || error.message);
        setError(
          error.response?.data?.message || "Error signing up. Try again."
        );
      }
    } else {
      try {
        await login({ email: normalizedEmail, password }, redirectPath);
        setSuccess("Login successful! Redirecting...");
        setTimeout(() => {
          onClose();
          navigate(redirectPath);
        }, 1000);
      } catch (error) {
        console.error("Login error:", error.response?.data || error.message);

        // Handle Google-only account case
        if (error.response?.data?.googleOnly) {
          setError(
            "This account was created with Google. Please use the Google Sign-In button below."
          );
        } else {
          setError("Invalid credentials!");
        }
      }
    }
  };

  const handleGoogleSuccess = async (response) => {
    console.log("Called handle google success", response);
    try {
      if (!response.credential) throw new Error("No credential received");
      // setLoading(true);
      const result = await loginWithGoogle(response.credential);
      if (result && result.token) {
        console.log("Google login succeeded, token received:", result.token);
        setSuccess("Google login successful! Redirecting...");
        setTimeout(() => {
          onClose();
          navigate(redirectPath);
        }, 1000);
      } else {
        throw new Error("No token received from Google login");
      }
    } catch (err) {
      console.error("Google login failed", err.response?.data || err.message);
      setError(err.message || "Google login failed. Please try again.");
    } finally {
      // setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setError("");
    setSuccess("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
  };

  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${config.apiBaseUrl}/api/auth/user/${user.userID}/setup-password`,
        { password: newPassword },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuccess(
        "Password set up successfully! You can now use email/password login."
      );
      setShowPasswordSetup(false);
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      console.error(
        "Password setup error:",
        error.response?.data || error.message
      );
      setError(
        error.response?.data?.message || "Error setting up password. Try again."
      );
    }
  };

  if (!open) return null;
  if (loading) return <div>Loading...</div>;

  return (
    <div className="auth-modal__overlay" onClick={onClose}>
      <div className="auth-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal__header">
          <a href="/" className="auth-modal__logo-link">
            <img
              src={logo}
              alt="Air Kitchenz Logo"
              className="auth-modal__logo-image"
            />
          </a>
          <h1 className="auth-modal__title">
            {mode === "login" ? "Welcome Back!" : "Join AirKitchenz!"}
          </h1>
          <button className="auth-modal__close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="auth-modal__content">
          {error && <div className="auth-modal__error-message">{error}</div>}
          {success && (
            <div className="auth-modal__success-message">{success}</div>
          )}
          <div className="auth-modal__google-login-container">
            <GoogleLogin
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
              onSuccess={handleGoogleSuccess}
              onError={() => {
                console.error("Google login failed");
                setError("Google login failed. Please try again.");
              }}
              shape="pill"
              width="100%"
            />
          </div>
          <div className="auth-modal__divider">
            <span>or</span>
          </div>
          {!success && (
            <form className="auth-modal__form" onSubmit={handleSubmit}>
              {mode === "signup" && (
                <>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="auth-modal__input"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="auth-modal__input"
                  />
                </>
              )}
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="auth-modal__input"
              />
              <div className="auth-modal__password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="auth-modal__input"
                />
                <button
                  type="button"
                  className="auth-modal__password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {mode === "signup" && (
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="auth-modal__input"
                />
              )}
              <button
                type="submit"
                className="auth-modal__submit-btn"
                disabled={loading}
              >
                {loading
                  ? "Loading..."
                  : mode === "login"
                  ? "Log In"
                  : "Sign Up"}
              </button>
              <div className="auth-modal__toggle-text" onClick={toggleMode}>
                {mode === "login"
                  ? "Don't have an account? Sign Up"
                  : "Already have an account? Log In"}
              </div>
            </form>
          )}

          {/* Password Setup Section for Google Users */}
          {user && user.googleId && !showPasswordSetup && (
            <div className="auth-modal__password-setup-section">
              <p className="auth-modal__password-setup-text">
                Want to use email/password login too? Set up a password for your
                account.
              </p>
              <button
                type="button"
                className="auth-modal__password-setup-btn"
                onClick={() => setShowPasswordSetup(true)}
              >
                Set Up Password
              </button>
            </div>
          )}

          {showPasswordSetup && (
            <form className="auth-modal__form" onSubmit={handlePasswordSetup}>
              <h3 className="auth-modal__password-setup-title">
                Set Up Password
              </h3>
              <p className="auth-modal__password-setup-description">
                Create a password so you can also log in with email and
                password.
              </p>
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="auth-modal__input"
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                className="auth-modal__input"
              />
              <button
                type="submit"
                className="auth-modal__submit-btn"
                disabled={loading}
              >
                {loading ? "Setting up..." : "Set Up Password"}
              </button>
              <button
                type="button"
                className="auth-modal__cancel-btn"
                onClick={() => {
                  setShowPasswordSetup(false);
                  setNewPassword("");
                  setConfirmNewPassword("");
                  setError("");
                }}
              >
                Cancel
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
